import { cloneElement, isValidElement, type ReactElement, type ReactNode, useCallback } from 'react'

import { Icon } from 'components/Icon'
import { useSidebar } from 'providers/SidebarProvider'
import { theme } from 'styles/theme/hav-theme'

import { SidebarItem } from './components/SidebarItem'
import * as S from './styles'

interface ItemProps {
  label: string
  icon: ReactNode
  active?: boolean
  onClick?: () => void
  route?: string
  disabled?: boolean
}

type SidebarChildren = ReactElement<ItemProps> | ReactElement<ItemProps>[]

export interface SidebarProps {
  children: SidebarChildren
  disabled?: boolean
}

export const Sidebar = ({ children, disabled = false }: SidebarProps) => {
  const { isCollapsed, toggleSidebar } = useSidebar()

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      toggleSidebar()
    },
    [toggleSidebar],
  )

  const ariaLabel = isCollapsed ? 'Expandir menu' : 'Recolher menu'

  const isItemElement = (element: ReactElement<ItemProps>): boolean => {
    return isValidElement(element) && 'label' in element.props && 'icon' in element.props
  }

  // NOTE: Clone item element to pass props to it, avoiding props drilling
  const cloneItemElement = useCallback(
    (element: ReactElement<ItemProps>) => {
      return cloneElement(element, {
        disabled: disabled || element.props.disabled,
        key: `sidebar-item-${element.props.label}`,
      })
    },
    [disabled],
  )

  const renderItems = useCallback(() => {
    if (!children) return null

    const isChildrenArray = Array.isArray(children)

    if (isChildrenArray) {
      return children.map((child) => {
        if (isItemElement(child)) {
          return cloneItemElement(child)
        }

        return child
      })
    }

    if (isItemElement(children)) {
      return cloneItemElement(children)
    }
  }, [children, disabled])

  return (
    <S.Container collapsed={isCollapsed} disabled={disabled}>
      <S.ExpandWrapper>
        <S.ExpandButton aria-label={ariaLabel} collapsed={isCollapsed} onClick={handleToggle}>
          <Icon color={theme.colors.primary.main} icon="arrow-forward" size="22" />
        </S.ExpandButton>
      </S.ExpandWrapper>
      <S.Divider />
      <S.ItemsContainer onClick={(e) => e.stopPropagation()}>{renderItems()}</S.ItemsContainer>
    </S.Container>
  )
}

Sidebar.Item = SidebarItem

const Divider = () => {
  return <S.Divider />
}

Sidebar.Divider = Divider
